// Copyright ©️ 2026 eVolve MEP, LLC

import { bendInputKeys } from '@evolvemep/api-ts';
import * as THREE from 'three';

import { isAlmostExactlyEqual } from 'helpers/isAlmostExactlyEqual';
import { isNotNil } from 'helpers/isNotNil';
import type { BendPreviewSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';

/**
 * Converts degrees to radians.
 * @param angle angle in degrees
 */
export const convertToRadians = (angle: number) => angle * (Math.PI / 180);

/**
 * Helper method for the tangent distance formula. Useful for placing "handles" on Bézier curves.
 * Used to calculate the distance from the bend vertex to its tangent points.
 * @param radius The radius of the curve.
 * @param theta The angle of the tangent.
 */
export const getTangentDistance = (radius: number, theta: number) => radius * Math.tan(theta / 2);

/**
 * Generates a {@link BendPreviewSpecifications} object with all dimensions set to 0.
 */
export const getBasicBendSpecifications = ({ bendRadius, conduitSize }: { bendRadius: number; conduitSize: number }) =>
  bendInputKeys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {
    bendRadius,
    conduitSize,
  } as BendPreviewSpecifications);

/**
 * Helper method to clean up materials from a THREE.js object.
 * @param material the material to dispose of, which may be single material or a list of materials.
 */
const disposeMaterial = (material: THREE.Material | THREE.Material[]) => {
  const disposeSingleMaterial = (singleMaterial: THREE.Material) => {
    Object.values(singleMaterial).forEach((value) => {
      if (value && typeof value === 'object' && 'dispose' in value && value instanceof THREE.Texture) {
        value.dispose();
      }
    });
    singleMaterial.dispose();
  };
  if (Array.isArray(material)) {
    material.forEach(disposeSingleMaterial);
  } else {
    disposeSingleMaterial(material);
  }
};

/**
 * Helper method to check if an object has a geometry property,
 * used to determine if we need to dispose of the geometry for cleanup.
 */
const objectHasGeometry = (object: THREE.Object3D): object is THREE.Object3D & { geometry: THREE.BufferGeometry } =>
  'geometry' in object && isNotNil(object.geometry);

/**
 * Helper method to dispose of a THREE.js object. Should be called whenever the object is no longer rendered.
 */
export const disposeObject3d = (child: THREE.Object3D) => {
  if (objectHasGeometry(child)) {
    child.geometry.dispose();
  }
  if ('material' in child && child.material) {
    disposeMaterial(child.material as THREE.Material | THREE.Material[]);
  }
};

/**
 * Helper method to dispose of a THREE.js group. Should be called whenever the group is no longer rendered.
 */
export const disposeGroup = (group: THREE.Group | THREE.Scene) => group.traverse(disposeObject3d);

/**
 * When uploading parts from Revit, data is being inconsistently represented as null or an empty string.
 * TODO: eliminate this function when that's fixed.
 */
export const isNotNilOrEmptyString = (value: string | undefined | null): value is string =>
  isNotNil(value) && value.trim().length > 0;

/**
 * Helper function to check if a value is close to zero.
 * Useful because floating point math can result in very small numbers that aren't quite zero, but should be treated as such.
 * @param value The value to check.
 * @param [precision=1e-4] The smallest absolute difference from zero to allow.
 */
export const isInsignificant = (value: number, precision?: number) => isAlmostExactlyEqual(value, 0, precision);
/**
 * Helper function to check if a value is NOT close to zero.
 * Useful because floating point math can result in very small numbers that aren't quite zero, but should be treated as such.
 * @param value The value to check.
 * @param [precision=1e-4] The smallest absolute difference from zero to allow.
 */
export const isSignificant = (value: number, precision?: number) => !isInsignificant(value, precision);

/**
 * Given a point, calculates the depth of the point in camera space and returns the view size at that depth.
 * @param camera The camera to use.
 * @param position The position of the point.
 */
export const getViewSizeAtPosition = (camera: THREE.PerspectiveCamera, position: THREE.Vector3) => {
  const viewZ = position.clone().applyMatrix4(camera.matrixWorldInverse).z;
  const depth = Math.max(-viewZ, 0.001);
  return camera.getViewSize(depth, new THREE.Vector2());
};

/**
 * Converts a THREE.Vector3 to a THREE.Vector2 by dropping Z
 * @param vector The vector to flatten.
 */
export const flattenVector = (vector: THREE.Vector3) => new THREE.Vector2(vector.x, vector.y);

/** The eight corners of a box. */
export const getBoxCorners = ({ min, max }: THREE.Box3) =>
  [min.x, max.x].flatMap((x) => [min.y, max.y].flatMap((y) => [min.z, max.z].map((z) => new THREE.Vector3(x, y, z))));

/**
 * Projects a world-space box into NDC space and returns the screen-aligned 2D bounds that contain it.
 *
 * All eight corners are projected rather than just `min`/`max`: projection is neither axis- nor
 * order-preserving, so those two alone can under-bound the box, and come back with `min > max` on
 * any axis the camera happens to mirror — which silently breaks `containsPoint` and `clampPoint`.
 * @param box The world-space box to project.
 * @param camera The camera to project through.
 */
export const projectBoxToNDC = (box: THREE.Box3, camera: THREE.Camera) => {
  const ndcBox = new THREE.Box2();
  getBoxCorners(box).forEach((corner) => ndcBox.expandByPoint(flattenVector(corner.project(camera))));
  return ndcBox;
};
